import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLangue, useTextes } from '../LangueContexte'
import { useVille } from '../VilleContexte'
import { Link, useNavigate } from 'react-router-dom'
import { NoteResume, VueReputation, useReputation } from './BlocReputation'
import { Etoiles } from './Etoiles'
import { Bandeau, Zone } from './Champs'
import { Badges } from './Vignette'
import { Bouton, EtatVide, Icone, Image, LienBouton, Modale, Puce, Squelette, SqueletteListe, SquelettePage } from '../ui'
import { dateCourte, montant, nombre } from '../formats'
import { CarteApprochee } from './CarteDifferee'
import { CandidaturesRecues, FormulaireCandidature } from './Candidatures'
import { CandidatsRecommandes, ModeDeVieAnnonce, PourquoiPas } from './ModeDeVieAnnonce'
import { ErreurApi } from '../api'
import { AutresAnnonces, EcranErreur } from './Erreurs'
import { mesVisites, ouvrirFil } from '../apiRelations'
import { ajouterFavori, retirerFavori } from '../apiRecherche'
import { ProposerVisite } from './Visites'
import { EQUIPEMENTS } from '../equipements'
import { TEXTES, interpoler, messageErreur, type Langue } from '../i18n'
import {
  changerEtatAnnonce,
  detailAnnonce,
  listerAnnonces,
  mesAnnonces,
  type DetailAnnonce,
  type FicheAnnonce,
  type MonAnnonce,
  type StatutAnnonce,
  type TransitionAnnonce,
  type TypeBail,
} from '../apiCatalogue'

/** Les initiales d'un nom affiché — deux lettres au plus, dans n'importe quel alphabet. */
function initiales(nom: string): string {
  return nom
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((mot) => mot.charAt(0))
    .join('')
    .toLocaleUpperCase()
}

/**
 * Le titre réduit à sa tête, pour le dernier maillon du fil d'Ariane — 14/09/2026.
 *
 * La maquette y met « Chambre meublée » quand son titre est « Chambre meublée 14 m² dans
 * colocation de 3 chambres » : le fil situe, il ne répète pas le H1 qui le suit immédiatement.
 * L'écran y recopiait le titre entier, tronqué d'un « … » à 22 rem — deux fois la même phrase,
 * dont l'une coupée.
 *
 * La coupe se fait sur la première virgule ou le premier tiret cadratin, qui est là où nos titres
 * passent du logement à ses précisions. Un titre sans ponctuation reste entier : mieux vaut un
 * maillon long qu'un maillon coupé au milieu d'un mot.
 */
function titreCourt(titre: string): string {
  const tete = titre.split(/[,—–]/)[0].trim()

  return tete.length === 0 ? titre : tete
}

/**
 * Une durée en minutes, dite dans l'unité qui se retient — 14/09/2026.
 *
 * `Intl.NumberFormat` en `style: 'unit'` plutôt que trois clés par langue et par pluriel : c'est
 * lui qui sait que l'arabe compte le duel et le pluriel séparément, et que trois heures s'y disent
 * « ٣ ساعات » quand huit s'y disent « ٨ ساعات ». Écrire ces règles dans un dictionnaire, c'est
 * réécrire le CLDR en moins bien.
 *
 * Les seuils sont ceux d'une phrase, pas d'une horloge : sous l'heure on compte en minutes, sous
 * la journée en heures, au-delà en jours. « 1 380 minutes » serait exact et illisible.
 */
function dureeApproximative(minutes: number, langue: string): string {
  const dire = (valeur: number, unit: 'minute' | 'hour' | 'day') =>
    new Intl.NumberFormat(langue, {
      style: 'unit',
      unit,
      unitDisplay: 'long',
      maximumFractionDigits: 0,
    }).format(valeur)

  if (minutes < 60) return dire(Math.max(1, Math.round(minutes)), 'minute')
  if (minutes < 1440) return dire(Math.round(minutes / 60), 'hour')

  return dire(Math.round(minutes / 1440), 'day')
}

function Vignette({ annonce, onOuvrir }: { annonce: FicheAnnonce; onOuvrir: (id: string) => void }) {
  return (
    <li className="vignette">
      <button type="button" onClick={() => onOuvrir(annonce.id)}>
        <Image
          mediaId={annonce.coverMediaId}
          alt=""
          ratio="4-3"
          sizes="(min-width: 60rem) 22rem, 92vw"
        />

        <span className="vignette__corps">
          <strong>{annonce.title}</strong>
          <span className="vignette__quartier">{annonce.district}</span>
          <span className="vignette__prix">
            {annonce.rentAllInclusive.toLocaleString('fr-MA')} {annonce.currencyCode} / mois
          </span>
          <Badges photos={annonce.hasOriginalPhotos} verifiee={annonce.isVerifiedOnSite} />
        </span>
      </button>
    </li>
  )
}

/**
 * Liste des annonces publiées.
 *
 * **Ni filtre, ni carte de recherche : c'est L3.** Cette liste existe pour qu'il y ait quelque
 * chose à modérer et à consulter, pas pour chercher.
 */
export function ListeAnnonces({ onOuvrir }: { onOuvrir: (id: string) => void }) {
  const t = useTextes()
  const [annonces, setAnnonces] = useState<FicheAnnonce[]>([])
  const [chargement, setChargement] = useState(true)

  useEffect(() => {
    void listerAnnonces()
      .then((page) => setAnnonces(page.items))
      .finally(() => setChargement(false))
  }, [])

  if (chargement) {
    return <p className="attente">{t.chargement}</p>
  }

  if (annonces.length === 0) {
    return (
      <EtatVide icone="maison" titre={t.catalogueVide}>
        {t.catalogueVideAide}
      </EtatVide>
    )
  }

  return (
    <ul className="vignettes">
      {annonces.map((annonce) => (
        <Vignette key={annonce.id} annonce={annonce} onOuvrir={onOuvrir} />
      ))}
    </ul>
  )
}

type Textes = (typeof TEXTES)['fr']
type Panneau = 'candidatures' | 'vie' | 'candidats'

/** L'état d'une annonce, dans une puce : c'est elle qui le dit, la carte ne le devine pas. */
function PuceStatut({ t, statut }: { t: Textes; statut: StatutAnnonce }) {
  switch (statut) {
    case 'Published':
      return <Puce ton="succes">{t.statutPublished}</Puce>
    case 'PendingModeration':
      return <Puce ton="attention">{t.statutPendingModeration}</Puce>
    case 'Rented':
      return <Puce ton="info">{t.statutRented}</Puce>
    case 'Rejected':
      return <Puce ton="danger">{t.statutRejected}</Puce>
    case 'Expired':
      return <Puce ton="attention">{t.statutExpired}</Puce>
    case 'Paused':
      return <Puce>{t.statutPaused}</Puce>
    case 'Draft':
      return <Puce>{t.statutDraft}</Puce>
    case 'Archived':
      return <Puce>{t.statutArchived}</Puce>
  }
}

/** Une mesure du tableau de bord. Un tiret tant que le chiffre n'est pas connu, jamais un zéro qui ment. */
function Mesure({ valeur, libelle, langue }: { valeur: number | null; libelle: string; langue: Langue }) {
  return (
    <div className="mesure">
      <span className="mesure__valeur">{valeur === null ? '–' : nombre(valeur, langue)}</span>
      <span className="mesure__libelle">{libelle}</span>
    </div>
  )
}

/**
 * Mes annonces — le tableau de bord du titulaire, revu le 13/09/2026 (maquette
 * `docs/ui/pwa/mes-annonces.html`).
 *
 * **Quatre états, quatre traitements, et c'est la puce qui dit l'état.** Le badge « vérifiée sur
 * place » vient du back-office, jamais d'ici (invariant 7) ; une annonce louée ou archivée ne
 * revient pas seule sur le marché, et la carte le dit avant qu'on ne le cherche.
 *
 * **Les chiffres viennent avec la liste.** Le nombre de candidatures ouvertes est calculé dans la
 * requête de `GET /me/listings` — pas un appel par annonce. Les visites à venir viennent de
 * `GET /me/visits`, que l'écran des visites lit déjà. Il n'existe pas de compteur de vues : la
 * maquette en montrait un, l'écran ne l'invente pas.
 *
 * **Ce qui n'y est pas : « Modifier ».** Le site n'a pas d'écran d'édition d'une annonce — le
 * parcours de publication crée, il ne relit pas ; l'application mobile a le sien. Un bouton qui
 * mènerait nulle part serait pire que son absence.
 *
 * Pas de proposition de visite ici : c'est le DEMANDEUR qui propose ses créneaux et le titulaire
 * de l'annonce qui en confirme un. Le domaine refuse l'inverse.
 */
export function MesAnnonces({ langue, moi }: { langue: Langue; moi: string | null }) {
  const t = useTextes()
  const [annonces, setAnnonces] = useState<MonAnnonce[] | null>(null)
  const [visitesAVenir, setVisitesAVenir] = useState<number | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)

  // Un seul panneau déplié à la fois : les candidatures reçues sont une liste, et deux listes
  // ouvertes côte à côte sur un écran de téléphone ne se lisent pas.
  const [panneau, setPanneau] = useState<{ id: string; quoi: Panneau } | null>(null)
  const [transition, setTransition] = useState<{
    annonce: MonAnnonce
    action: TransitionAnnonce
    libelle: string
  } | null>(null)
  const [occupe, setOccupe] = useState(false)

  const signaler = useCallback(
    (cause: unknown) => setErreur(messageErreur(langue, cause instanceof ErreurApi ? cause.code : 'internal')),
    [langue],
  )

  const charger = useCallback(async () => {
    try {
      setAnnonces((await mesAnnonces(1, 50)).items)
    } catch (cause) {
      signaler(cause)
    }
  }, [signaler])

  useEffect(() => {
    void charger()
  }, [charger])

  useEffect(() => {
    if (moi === null) {
      return
    }

    mesVisites()
      .then((visites) =>
        setVisitesAVenir(visites.filter((visite) => visite.hostUserId === moi && visite.status === 'Confirmed').length),
      )
      .catch(() => setVisitesAVenir(null))
  }, [moi])

  async function appliquer() {
    if (transition === null) {
      return
    }

    setOccupe(true)
    setErreur(null)

    try {
      await changerEtatAnnonce(transition.annonce.id, transition.action)
      setTransition(null)
      await charger()
    } catch (cause) {
      signaler(cause)
    } finally {
      setOccupe(false)
    }
  }

  const basculer = (id: string, quoi: Panneau) =>
    setPanneau(panneau !== null && panneau.id === id && panneau.quoi === quoi ? null : { id, quoi })
  const ouvert = (id: string, quoi: Panneau) => panneau !== null && panneau.id === id && panneau.quoi === quoi

  const compte = (statut: StatutAnnonce) => (annonces ?? []).filter((annonce) => annonce.status === statut).length
  const candidatures = (annonces ?? []).reduce((somme, annonce) => somme + annonce.openApplicationCount, 0)

  return (
    <section className="mes-annonces">
      <div className="page-entete">
        <div className="page-entete__rangee">
          <h2>{t.annoncesMes}</h2>
          <LienBouton to="/publier">
            <Icone nom="plus" taille="sm" /> {t.navPublier}
          </LienBouton>
        </div>
      </div>

      {erreur !== null && <Bandeau ton="erreur">{erreur}</Bandeau>}

      {annonces === null ? (
        <SqueletteListe />
      ) : annonces.length === 0 ? (
        <EtatVide
          icone="maison"
          titre={t.annoncesAucune}
          action={
            <LienBouton to="/publier">
              <Icone nom="plus" taille="sm" /> {t.navPublier}
            </LienBouton>
          }
        >
          {t.annoncesAucuneAide}
        </EtatVide>
      ) : (
        <>
          <div className="mesures">
            <Mesure valeur={compte('Published')} libelle={t.annoncesMesureEnLigne} langue={langue} />
            <Mesure valeur={candidatures} libelle={t.annoncesMesureCandidatures} langue={langue} />
            <Mesure valeur={visitesAVenir} libelle={t.annoncesMesureVisites} langue={langue} />
            <Mesure valeur={compte('PendingModeration')} libelle={t.annoncesMesureEnAttente} langue={langue} />
          </div>

          <ul className="mes-annonces__liste">
            {annonces.map((annonce) => (
              <CarteMienne
                key={annonce.id}
                t={t}
                langue={langue}
                annonce={annonce}
                ouvert={(quoi) => ouvert(annonce.id, quoi)}
                onBasculer={(quoi) => basculer(annonce.id, quoi)}
                onTransition={(action, libelle) => setTransition({ annonce, action, libelle })}
              />
            ))}
          </ul>
        </>
      )}

      {transition !== null && (
        <Modale
          titre={interpoler(t.annonceEtatConfirmer, { action: transition.libelle })}
          onFermer={() => setTransition(null)}
          pied={
            <>
              <Bouton apparence="discret" onClick={() => setTransition(null)}>
                {t.annuler}
              </Bouton>
              <Bouton
                apparence={transition.action === 'archive' ? 'danger' : 'principal'}
                charge={occupe}
                onClick={() => void appliquer()}
              >
                {transition.libelle}
              </Bouton>
            </>
          }
        >
          <div className="pile">
            <p className="fort">{transition.annonce.title}</p>
            {(transition.action === 'rented' || transition.action === 'archive') && (
              <Bandeau ton="attention" icone={<Icone nom="info" />}>
                {t.annonceEtatAide}
              </Bandeau>
            )}
          </div>
        </Modale>
      )}
    </section>
  )
}

/**
 * Une annonce du titulaire : vignette, état, ligne de faits, badges, et les gestes que son état
 * permet. Les transitions passent par une confirmation ; les panneaux se déplient sous la carte.
 */
function CarteMienne({
  t,
  langue,
  annonce,
  ouvert,
  onBasculer,
  onTransition,
}: {
  t: Textes
  langue: Langue
  annonce: MonAnnonce
  ouvert: (quoi: Panneau) => boolean
  onBasculer: (quoi: Panneau) => void
  onTransition: (action: TransitionAnnonce, libelle: string) => void
}) {
  const statut = annonce.status
  const enLigne = statut === 'Published'
  const enPause = statut === 'Paused'
  const estompee = statut === 'Archived' || statut === 'Expired' || statut === 'Rented' || statut === 'Rejected'

  const faits = [
    `${montant(annonce.rentAllInclusive, annonce.currencyCode, langue)} ${t.annonceParMois}`,
    annonce.district,
    annonce.publishedAt !== null
      ? interpoler(t.annoncesPublieeLe, { date: dateCourte(annonce.publishedAt, langue) })
      : interpoler(t.annoncesCreeeLe, { date: dateCourte(annonce.createdAt, langue) }),
    annonce.openApplicationCount > 0
      ? interpoler(
          annonce.openApplicationCount === 1 ? t.annoncesCandidatureEnCours : t.annoncesCandidaturesEnCours,
          { n: annonce.openApplicationCount },
          langue,
        )
      : null,
  ].filter((fait): fait is string => fait !== null)

  const Panneau = ({ quoi, libelle }: { quoi: Panneau; libelle: string }) => (
    <Bouton apparence="discret" taille="sm" onClick={() => onBasculer(quoi)} className={ouvert(quoi) ? 'est-ouvert' : undefined}>
      {libelle}
    </Bouton>
  )

  return (
    <li className={estompee ? 'bloc bloc--serre mes-annonces__carte est-estompee' : 'bloc bloc--serre mes-annonces__carte'}>
      <div className="vignette-annonce">
        <Image
          mediaId={annonce.coverMediaId}
          alt=""
          ratio="4-3"
          sizes="7rem"
          vide={<Icone nom="image" />}
        />
        <div className="pile pile--serree">
          <div className="rangee rangee--espace">
            <h3>{annonce.title}</h3>
            <PuceStatut t={t} statut={statut} />
          </div>
          <p className="secondaire">{faits.join(' · ')}</p>
          <Badges photos={annonce.hasOriginalPhotos} verifiee={annonce.isVerifiedOnSite} />

          {statut === 'Rented' && (
            <Bandeau ton="info" icone={<Icone nom="info" />}>
              {t.annonceEtatAide}
            </Bandeau>
          )}

          {statut !== 'Archived' && (
            <div className="rangee rangee--serree mes-annonces__actions" role="group" aria-label={t.annoncesActions}>
              {enLigne && (
                <LienBouton to={`/annonces/${annonce.id}`} apparence="secondaire" taille="sm">
                  {t.annonceVoir}
                </LienBouton>
              )}
              {statut === 'Expired' && (
                <Bouton
                  apparence="secondaire"
                  taille="sm"
                  onClick={() => onTransition('confirm-availability', t.annonceConfirmerDisponibilite)}
                >
                  {t.annonceConfirmerDisponibilite}
                </Bouton>
              )}
              {(enLigne || enPause || statut === 'Rented') && <Panneau quoi="candidatures" libelle={t.candidatures} />}
              {enLigne && <Panneau quoi="candidats" libelle={t.candidatsRecommandes} />}
              {(enLigne || enPause || statut === 'Draft' || statut === 'PendingModeration') && (
                <Panneau quoi="vie" libelle={t.modeDeVieAnnonce} />
              )}
              {enLigne && (
                <Bouton apparence="discret" taille="sm" onClick={() => onTransition('pause', t.annonceDepublier)}>
                  {t.annonceDepublier}
                </Bouton>
              )}
              {enPause && (
                <Bouton apparence="discret" taille="sm" onClick={() => onTransition('resume', t.annonceRemettreEnLigne)}>
                  {t.annonceRemettreEnLigne}
                </Bouton>
              )}
              {(enLigne || enPause) && (
                <Bouton apparence="discret" taille="sm" onClick={() => onTransition('rented', t.annonceMarquerLouee)}>
                  {t.annonceMarquerLouee}
                </Bouton>
              )}
              <Bouton apparence="discret" taille="sm" onClick={() => onTransition('archive', t.annonceArchiver)}>
                {t.annonceArchiver}
              </Bouton>
            </div>
          )}
        </div>
      </div>

      {ouvert('candidatures') && (
        <div className="mes-annonces__panneau">
          <CandidaturesRecues langue={langue} annonceId={annonce.id} />
        </div>
      )}
      {ouvert('candidats') && (
        <div className="mes-annonces__panneau">
          <CandidatsRecommandes langue={langue} annonceId={annonce.id} />
        </div>
      )}
      {ouvert('vie') && (
        <div className="mes-annonces__panneau">
          <ModeDeVieAnnonce langue={langue} annonceId={annonce.id} />
        </div>
      )}
    </li>
  )
}

/** Fiche complète d'une annonce publiée. */
export function FicheDetaillee({
  id,
  onRetour,
  langue,
  connecte,
}: {
  id: string
  onRetour: () => void
  langue: Langue
  connecte: boolean
}) {
  const t = TEXTES[langue]
  const ville = useVille()

  const [annonce, setAnnonce] = useState<DetailAnnonce | null>(null)
  /** La cause de l'échec de chargement — c'est elle qui choisit l'écran d'erreur. */
  const [echec, setEchec] = useState<unknown>(null)

  const charger = useCallback(() => {
    setEchec(null)

    void detailAnnonce(id)
      .then(setAnnonce)
      .catch((cause: unknown) => setEchec(cause ?? new Error()))
  }, [id])

  useEffect(charger, [charger])

  // Les équipements déclarés, dans l'ordre du référentiel et non dans celui du tableau reçu : deux
  // annonces qui portent les mêmes équipements les listent donc pareil, ce qui les rend
  // comparables d'un coup d'œil.
  const equipements = EQUIPEMENTS.filter((equipement) =>
    annonce?.amenityCodes.includes(equipement.code),
  )

  /*
    **Une seule lecture de la réputation du logement, deux affichages** — ticket `fo-annonce-05`.

    La note part dans la colonne de droite, sous le propriétaire ; les avis restent dans le corps,
    où ils ont la largeur de se lire. Deux composants qui liraient chacun de leur côté doubleraient
    une réponse qui porte les avis eux-mêmes.

    Déclaré ICI, avant les retours anticipés : l'identifiant du bien n'existe qu'une fois l'annonce
    chargée, et un crochet ne se déclare pas sous condition. Le crochet accepte donc `null`.
  */
  const cibleBien = useMemo(
    () => (annonce === null ? null : { nature: 'bien' as const, id: annonce.propertyId }),
    [annonce],
  )

  const reputationLogement = useReputation(cibleBien, langue)

  /*
    **L'état d'erreur est un écran, pas un message** — tickets `fo-annonce-03` et `fo-annonce-10`.

    Ces deux tickets décrivent une fiche sans fil d'Ariane et sans bouton « mettre en favoris ».
    Or les deux existent depuis la correction de `fo-annonce-01` : ce qui a été vu n'est pas la
    fiche, c'est CET écran-là — celui qui s'affiche quand l'annonce n'a pas pu être chargée.

    Il portait un `<button>Retour</button>` en dur, non traduit, sans style et sans repère. Sur un
    écran d'erreur, le seul élément présent est aussi le seul indice : s'il est pauvre, on conclut
    que la page est pauvre. Il porte donc maintenant le même fil d'Ariane que la fiche — qui dit
    au moins d'où l'on vient — et un bouton qui dit où il mène.

    **Revu le 16/09/2026** (`docs/ui/pwa/introuvable.html`, état 2) : l'écran lit la cause. Une
    annonce que l'API ne rend pas — retirée, louée, jamais publiée, identifiant tronqué — est un
    état vide qui le dit, suivi de trois autres annonces de la même ville ; une API muette est
    « le service ne répond pas », avec « Réessayer » qui relance l'appel.
  */
  if (echec !== null) {
    return (
      <section className="fiche">
        <nav className="fil-ariane" aria-label={t.annonceFilAriane}>
          <button type="button" className="lien" onClick={onRetour}>
            {t.rechercheChercher}
          </button>
          <Icone nom="chevron" taille="sm" miroir />
          <span aria-current="page">{t.candidatureAnnonce}</span>
        </nav>

        <EcranErreur
          cause={echec}
          objet="annonce"
          onReessayer={charger}
          suite={<AutresAnnonces titre={t.erreurAnnonceSuite} toutes={t.erreurAnnonceToutes} />}
        />
      </section>
    )
  }

  /*
    **Le squelette de la fiche** — ticket `fo-annonce-02`.

    Il n'emprunte pas les deux formes génériques : la fiche a une anatomie qui n'appartient qu'à
    elle — une mosaïque de photos, puis deux colonnes dont l'une est collante. Un squelette de
    liste y annoncerait une page qui ne viendra pas, et la mise en page sauterait à l'arrivée des
    données, ce qui est exactement ce qu'un squelette existe pour éviter.
  */
  if (annonce === null) {
    return (
      <article className="fiche" aria-busy="true">
        <Squelette forme="texte" largeur="40%" />

        <div className="squelette-entete">
          <Squelette forme="titre" largeur="55%" />
          <Squelette forme="texte" largeur="35%" />
        </div>

        <div className="mosaique mosaique--5 squelette-mosaique">
          {Array.from({ length: 5 }, (_, rang) => (
            <span key={rang} className="squelette mosaique__case" aria-hidden="true" />
          ))}
        </div>

        <div className="fiche__colonnes">
          <div className="fiche__corps">
            <SquelettePage sections={3} />
          </div>
          <aside className="fiche__aparte">
            <div className="bloc squelette-bloc">
              <Squelette forme="titre" largeur="50%" />
              <Squelette forme="texte" />
              <Squelette forme="texte" largeur="80%" />
              <Squelette forme="texte" largeur="80%" />
            </div>
          </aside>
        </div>
      </article>
    )
  }

  return (
    <article className="fiche">
      {/*
        **Le fil d'Ariane** — ticket `fo-annonce-01`. Un bouton « retour » dit d'où l'on vient
        sans dire où l'on est : sur une fiche ouverte depuis un lien partagé, il ne dit rien du
        tout. Le fil situe l'annonce dans le catalogue, et son premier maillon ramène à la
        recherche — ce que le bouton faisait, en moins précis.

        Le dernier maillon n'est pas un lien : c'est la page courante, et un lien vers soi-même
        est une impasse que les lecteurs d'écran annoncent quand même.

        **Le quartier est un lien** — ticket `fo-annonce-03`, second point. Un fil d'Ariane dont
        les maillons intermédiaires ne mènent nulle part n'est pas un fil d'Ariane : c'est une
        phrase. Celui-ci rouvre la recherche filtrée sur le quartier, c'est-à-dire « d'autres
        comme celle-ci », qui est la question qu'on se pose en refermant une fiche.

        Le libellé brut part tel quel dans `district` : le serveur normalise ce qu'il reçoit avant
        de le comparer à la colonne normalisée, donc l'orthographe de la fiche retrouve bien la
        facette du filtre — même quand elles diffèrent d'un accent.
      */}
      <nav className="fil-ariane" aria-label={t.annonceFilAriane}>
        <button type="button" className="lien" onClick={onRetour}>
          {t.rechercheChercher}
        </button>
        <Icone nom="chevron" taille="sm" miroir />
        {/* Avec la ville (L13) : un quartier ne se cherche que dans la sienne. */}
        <Link to={`/recherche?city=${encodeURIComponent(annonce.cityCode)}&district=${encodeURIComponent(annonce.district)}`}>
          {annonce.district}
        </Link>
        <Icone nom="chevron" taille="sm" miroir />
        <span aria-current="page">{titreCourt(annonce.title)}</span>
      </nav>

      <header className="fiche__entete">
        <div>
          <h2>{annonce.title}</h2>
          {/* La ville dans la langue de l'interface, depuis le référentiel — `city` n'est que le français. */}
          <p className="fiche__lieu">
            {annonce.district} · {ville.libelle(annonce.cityCode) || annonce.city}
          </p>
          <Badges photos={annonce.hasOriginalPhotos} verifiee={annonce.isVerifiedOnSite} />
        </div>

        {/*
          **Partager et Enregistrer, en haut à droite** — ticket `fo-annonce-01`, et c'est la
          place que leur donne la maquette. Les deux gestes se font au moment où l'on décide que
          l'annonce mérite qu'on y revienne, c'est-à-dire en la parcourant — pas après avoir
          déroulé la fiche jusqu'au bout.
        */}
        <div className="fiche__actions">
          <Partager titre={annonce.title} />
          <Enregistrer id={id} initial={annonce.isFavorite} connecte={connecte} />
        </div>
      </header>

      <Galerie photos={annonce.photos} />

      <div className="fiche__colonnes">
        <div className="fiche__corps">
          {/*
            Dire honnêtement ce qui n'a pas été relu.

            En publication ouverte, une annonce est visible sans avoir été examinée. Le taire
            laisserait croire à une validation qui n'a pas eu lieu — exactement le reproche fait
            aux plateformes que le §1.1 de la spec décrit, et que ce produit prétend corriger.
          */}
          {!annonce.hasBeenReviewed && <Bandeau ton="info">{t.annonceNonRelue}</Bandeau>}

          <section className="fiche__section">
            <h3>{t.annonceCaracteristiques}</h3>
            <div className="fiche__caracteristiques">
              <p className="fiche__caracteristique">
                <Icone nom="maison" />
                <span>
                  <b>{BAUX[annonce.leaseType](t)}</b>
                  <span>{t.annonceTypeBail}</span>
                </span>
              </p>
              <p className="fiche__caracteristique">
                <Icone nom="groupe" />
                <span>
                  <b>
                    {nombre(annonce.roomsOccupied, langue)} / {nombre(annonce.roomsOffered, langue)}
                  </b>
                  <span>{t.annonceChambresOccupees}</span>
                </span>
              </p>
              <p className="fiche__caracteristique">
                <Icone nom="calendrier" />
                <span>
                  <b>{dateCourte(annonce.availableFrom, langue)}</b>
                  <span>{t.annonceDisponibleDu}</span>
                </span>
              </p>
              <p className="fiche__caracteristique">
                <Icone nom="horloge" />
                <span>
                  <b>
                    {nombre(annonce.minimumStayMonths, langue)} {t.annonceMois}
                  </b>
                  <span>{t.annonceDureeMinimale}</span>
                </span>
              </p>
            </div>
          </section>

          <section className="fiche__section">
            <h3>{t.annonceDescription}</h3>
            <p className="fiche__description">{annonce.description}</p>
          </section>

          {/*
            **Les équipements** — ticket `fo-annonce-01`. `amenityCodes` arrivait dans la fiche
            depuis L2 sans que rien ne l'affiche : on pouvait les déclarer à la publication, les
            filtrer à la recherche, et ne pas les voir sur l'annonce trouvée. C'est le seul des
            huit écarts relevés qui faisait perdre une information déjà saisie.

            Les libellés viennent du référentiel partagé, jamais d'une liste écrite ici : c'est ce
            qui garantit que « Wi-Fi fibre » se dit pareil à la publication, au filtre et ici.
          */}
          {equipements.length > 0 && (
            <section className="fiche__section">
              <h3>{t.annonceEquipements}</h3>
              {/*
                **Une icône par nature, et les absents avec** — ticket `fo-annonce-09`, et c'est
                ce que montre la maquette.

                Sept coches identiques obligent à lire sept libellés ; sept pictogrammes se
                balaient. Et les équipements ABSENTS figurent aussi, barrés : savoir qu'il n'y a
                pas de parking est une information, alors qu'une liste où il ne figure pas laisse
                dans le doute — le propriétaire a-t-il oublié de le cocher ?
              */}
              <div className="fiche__caracteristiques">
                {EQUIPEMENTS.map((equipement) => {
                  const present = annonce.amenityCodes.includes(equipement.code)

                  return (
                    <p
                      className={
                        present
                          ? 'fiche__caracteristique'
                          : 'fiche__caracteristique fiche__caracteristique--absent'
                      }
                      key={equipement.code}
                    >
                      <Icone nom={present ? equipement.icone : 'croix'} />
                      <span>
                        <b>{t[equipement.libelle]}</b>
                        {!present && <span>{t.annonceEquipementAbsent}</span>}
                      </span>
                    </p>
                  )
                })}
              </div>
            </section>
          )}

          {/*
            **L'ordre des blocs suit la maquette** — ticket `fo-annonce-05`.

            Il ne s'agit pas d'esthetique mais de l'ordre dans lequel les questions se posent :
            qu'est-ce que c'est, a quoi ca ressemble, qu'est-ce qu'il y a dedans, est-ce que ca me
            correspond, ou est-ce, comment on y vit, et qu'en ont pense ceux qui y sont passes.

            Trois blocs etaient a la mauvaise place. L'explication du score arrivait EN DERNIER,
            apres la carte — alors que c'est elle qui repond a « pourquoi celle-ci ? », question
            qu'on se pose en decouvrant l'annonce et non en la quittant. Les avis, eux, etaient
            coinces entre les regles et la carte : ils closent la lecture, ils ne l'interrompent
            pas.
          */}

          {/* Le seul lecteur d'un resultat ELIMINE : les elimines ne sont pas persistes, ce
              panneau recalcule le couple a la volee (conception L5, §1.7). */}
          {connecte && <PourquoiPas langue={langue} annonceId={id} />}

          <section className="fiche__section">
            <h3>{t.annonceSituation}</h3>
            <CarteApprochee latitude={annonce.publicLatitude} longitude={annonce.publicLongitude} />
          </section>

          {annonce.houseRulesSummary !== null && (
            <section className="fiche__section">
              <h3>{t.annonceRegles}</h3>
              <p>{annonce.houseRulesSummary}</p>
            </section>
          )}



          {/*
            **Les avis, et non la note** — ticket `fo-annonce-05`, réouverture.

            La note du logement est passée dans la colonne de droite, sous celle du propriétaire :
            les deux réputations que la fiche porte sont distinctes (conception L8, §1.9) mais se
            lisent ensemble, et un écran de défilement entre elles fait qu'on n'en lit qu'une.

            Ce qui reste ici, ce sont les avis eux-mêmes : ils demandent de la largeur, et la
            colonne collante n'en a pas. C'est aussi la place que leur donne la maquette. Le titre
            change en conséquence — cette section n'annonce plus un chiffre, elle annonce ce que
            les gens ont écrit.
          */}
          <section className="fiche__section">
            <VueReputation etat={reputationLogement} langue={langue} titre={t.avisTitre} />
          </section>
        </div>

        {/*
          La colonne collante.

          La note de conception la prevoyait « visiblement vide jusqu'a L4 » : elle ne l'est pas,
          L4 ayant livre la messagerie et la candidature. Le prix, ce qu'il recouvre, et l'action
          restent visibles pendant qu'on lit la description — c'est-a-dire au moment ou la
          question « combien, et comment je contacte » se pose.
        */}
        <aside className="fiche__aparte">
          <div className="bloc">
            <p className="fiche__prix">
              <bdi>{montant(annonce.rentAllInclusive, annonce.currencyCode, langue)}</bdi>{' '}
              <span>{t.annonceParMois}</span>
            </p>
            <p className="aide">{t.annonceChargesComprises}</p>

            <dl className="fiche__lignes">
              <div className="fiche__ligne">
                <dt>{t.annonceLoyerHorsCharges}</dt>
                <dd>
                  <bdi>{montant(annonce.rentExcludingCharges, annonce.currencyCode, langue)}</bdi>
                </dd>
              </div>
              <div className="fiche__ligne">
                <dt>{t.annonceCharges}</dt>
                <dd>
                  <bdi>{montant(annonce.chargesTotal, annonce.currencyCode, langue)}</bdi>
                </dd>
              </div>

              {/*
                **Le détail des charges, sous leur somme** — ticket `fo-annonce-01`.

                Il occupait une section entière du corps, loin du total qu'il décompose : on lisait
                « Charges : 250 MAD » dans l'aparté, et il fallait chercher ailleurs ce que ces
                250 MAD recouvrent. Une somme et son détail se lisent ensemble ou ne se lisent pas.

                En petit et en retrait : ce sont des sous-lignes du total, pas des lignes de même
                rang. Les mettre au même niveau donnerait cinq montants dont on ne saurait plus
                lequel s'additionne à quoi.
              */}
              {annonce.charges.map((charge) => (
                <div className="fiche__ligne fiche__ligne--detail" key={charge.type}>
                  <dt>
                    {charge.type}
                    {charge.isEstimated && <span className="aide"> · {t.annonceEstime}</span>}
                  </dt>
                  <dd>
                    <bdi>{montant(charge.amount, annonce.currencyCode, langue)}</bdi>
                  </dd>
                </div>
              ))}

              <div className="fiche__ligne">
                <dt>{t.annonceDepot}</dt>
                <dd>
                  <bdi>{montant(annonce.securityDeposit, annonce.currencyCode, langue)}</bdi>
                </dd>
              </div>
              <div className="fiche__ligne">
                <dt>{t.annonceDureeMinimale}</dt>
                <dd>
                  {nombre(annonce.minimumStayMonths, langue)} {t.annonceMois}
                </dd>
              </div>
              <div className="fiche__ligne">
                <dt>{t.annonceDisponibleDu}</dt>
                <dd>{dateCourte(annonce.availableFrom, langue)}</dd>
              </div>
            </dl>

            <MiseEnRelation id={id} titre={annonce.title} langue={langue} connecte={connecte} />

            {/*
              Bandeau anti-arnaque du §5.2d : au plus près du geste de contact, non fermable.

              **Décollé du bouton qui le précède** — ticket `fo-annonce-13`. Le `.bloc` n'a pas de
              gouttière : tout ce qu'on y pose s'empile bord à bord. Un avertissement collé sous
              un bouton se lit comme la légende de ce bouton, alors qu'il porte sur toute la
              relation — et surtout, il se saute, parce que rien ne dit qu'il commence.
            */}
            <div className="fiche__avertissement">
              <Bandeau ton="erreur">{t.antiArnaque}</Bandeau>
            </div>
          </div>

          {/*
            **Le bloc du bailleur, à droite** — ticket `fo-annonce-01`, et c'est la place que lui
            donne la maquette. Il vivait au milieu du corps, entre la description et la carte : on
            le rencontrait par hasard en faisant défiler, alors que sa question — « à qui ai-je
            affaire ? » — se pose au moment de cliquer sur « Contacter », qui est juste au-dessus.

            DEUX notes distinctes, jamais fusionnées (conception L8, §1.9) : celle du propriétaire
            vaut sur l'ensemble de ses biens, celle du logement n'existe qu'après que quelqu'un y a
            vécu. Elles sont maintenant l'une sous l'autre — ticket `fo-annonce-05`, réouverture —
            parce qu'elles se lisent ensemble : « à qui ai-je affaire » et « comment est le
            logement » sont la même question posée deux fois. Les afficher dans la même forme est
            ce qui permet de les comparer sans les confondre.

            **Le bloc dit QUI publie avant de dire ce qu'on en pense** (14/09/2026). Il n'en disait
            rien : « Propriétaire — Nouveau », sur l'écran où l'on décide d'écrire à un inconnu. Un
            nom affiché et une ancienneté n'ouvrent aucune coordonnée — le masquage de L4 porte sur
            le téléphone, l'e-mail et l'adresse exacte — et l'identifiant reste non publié : le
            catalogue ne donne toujours pas le lien bien → propriétaire.

            **Le nom arrive déjà abrégé** — « Yassine E. ». C'est l'API qui coupe, jamais l'écran :
            une troncature d'affichage laisserait le nom entier dans la réponse HTTP, donc à la
            portée de n'importe quel client du catalogue.

            **Le délai de réponse est MESURÉ** (14/09/2026) : médiane des premières réponses du
            bailleur sur ses fils récents, tue sous trois échanges. La maquette annonçait « répond
            en moyenne en 4 heures » ; l'écran dit « en général », parce qu'une médiane n'est pas
            une moyenne et qu'une seule réponse très tardive ne doit pas déplacer le chiffre.
          */}
          {(annonce.landlord || annonce.landlordRating) && (
            <div className="bloc fiche__bailleur">
              {annonce.landlord ? (
                <div className="fiche__bailleur-identite">
                  <span className="avatar avatar--lg" aria-hidden="true">
                    {initiales(annonce.landlord.displayName)}
                  </span>
                  <div>
                    <p className="fiche__bailleur-nom">{annonce.landlord.displayName}</p>
                    <p className="secondaire">
                      {interpoler(t.annonceBailleurDepuis, {
                        annee: new Intl.DateTimeFormat(langue, { year: 'numeric' }).format(
                          new Date(annonce.landlord.memberSince),
                        ),
                      })}
                    </p>
                    {annonce.landlordRating?.isDisplayable ? (
                      <p className="fiche__note-bailleur">
                        <Etoiles
                          note={annonce.landlordRating.rating ?? 0}
                          langue={langue}
                          libelle={t.avisNoteBailleur}
                        />
                        {/* Le CHIFFRE a cote des etoiles, comme la maquette. Cinq etoiles dont
                            quatre pleines se lisent « environ quatre » ; « 3,7 » se lit 3,7, et
                            c'est la valeur sur laquelle on decide d'ecrire ou non. */}
                        <span className="note__valeur">
                          {new Intl.NumberFormat(langue, {
                            minimumFractionDigits: 1,
                            maximumFractionDigits: 1,
                          }).format(annonce.landlordRating.rating ?? 0)}
                        </span>
                        <span className="aide">
                          {nombre(annonce.landlordRating.reviewCount, langue)} {t.avisNombre}
                        </span>
                      </p>
                    ) : (
                      /* « Nouveau » prend la PLACE de la note, sous le nom : un bailleur sans avis
                         n'est pas un bailleur sans nom, et le bloc garde la même forme dans les
                         deux cas. Sous trois avis, aucun chiffre ne sort (spec 8.2-6). */
                      <p className="aide">{t.avisNouveau}</p>
                    )}
                  </div>
                </div>
              ) : (
                <p className="fiche__bailleur-titre">{t.avisNoteBailleur}</p>
              )}

              {/* Rien à dire vaut mieux qu'une promesse : sous trois échanges mesurés, l'API rend
                  `null` et la ligne n'existe pas. */}
              {annonce.landlord?.typicalReplyMinutes != null && (
                <p className="secondaire fiche__bailleur-delai">
                  {interpoler(t.annonceBailleurRepond, {
                    duree: dureeApproximative(annonce.landlord.typicalReplyMinutes, langue),
                  })}
                </p>
              )}

              {/* Sans identité — la fiche que le titulaire relit —, le bloc se réduit à la note. */}
              {!annonce.landlord && annonce.landlordRating && !annonce.landlordRating.isDisplayable && (
                <p className="aide fiche__bailleur-nouveau">{t.avisNouveau}</p>
              )}
            </div>
          )}

          {/* La note du logement, sous celle du propriétaire. Les avis qu'elle résume restent
              dans le corps, où ils ont la largeur de se lire. */}
          <NoteResume etat={reputationLogement} langue={langue} titre={t.avisNoteLogement} />
        </aside>
      </div>
    </article>
  )
}

/** Le type de bail, en clair. Deux valeurs, et le domaine n'en prevoit pas d'autre. */
const BAUX: Record<TypeBail, (t: Record<string, string>) => string> = {
  SingleJoint: (t) => t.bailSolidaire,
  Individual: (t) => t.bailIndividuel,
}

/**
 * Contacter, ou candidater — L4.
 *
 * **Aucun contrôle de propriété ici.** L'API refuse d'écrire ou de candidater sur sa propre annonce
 * avec un code stable, et ce code devient une phrase par `messageErreur`. Reproduire la règle dans
 * l'écran demanderait de savoir qui possède l'annonce — c'est-à-dire de publier le lien entre un
 * bien et son propriétaire, exactement ce que le catalogue public ne fait pas.
 *
 * Le premier message est obligatoire : un fil vide n'apprend rien au propriétaire, et un bouton
 * « contacter » sans texte ne produirait que des conversations que personne n'ouvre.
 */
function MiseEnRelation({
  id,
  titre,
  langue,
  connecte,
}: {
  id: string
  titre: string
  langue: Langue
  connecte: boolean
}) {
  const t = TEXTES[langue]
  const navigate = useNavigate()

  const [corps, setCorps] = useState('')
  const [envoi, setEnvoi] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)
  // `null` = aucun formulaire ouvert, et c'est l'état de DÉPART. La fiche s'ouvre sur l'annonce et
  // non sur un formulaire de contact : on décide d'écrire après avoir lu, et un champ de saisie
  // déplié d'office pousse tout le reste vers le bas sans avoir été demandé.
  const [vue, setVue] = useState<'message' | 'candidature' | 'visite' | null>(null)

  if (!connecte) {
    return (
      <p className="fiche__relation">
        <Link
          className="bouton bouton--lg bouton--bloc"
          to={`/connexion?retour=${encodeURIComponent(`/annonces/${id}`)}`}
        >
          {t.contacter}
        </Link>
      </p>
    )
  }

  const contacter = async () => {
    const texte = corps.trim()

    if (texte.length === 0 || envoi) {
      return
    }

    setEnvoi(true)
    setErreur(null)

    try {
      const fil = await ouvrirFil(id, texte)
      navigate(`/messages/${fil.id}`)
    } catch (cause: unknown) {
      setErreur(messageErreur(langue, cause instanceof ErreurApi ? cause.code : 'internal'))
    } finally {
      setEnvoi(false)
    }
  }

  return (
    <section className="fiche__relation" aria-label={titre}>
      {/*
        **Trois boutons empiles, avec une hierarchie** — ticket `fo-annonce-08`.

        C'etaient trois liens de meme poids dans une rangee d'onglets. Or ces trois gestes ne se
        valent pas et n'arrivent pas au meme moment : on ecrit avant de visiter, on visite avant
        de candidater. La maquette les empile et leur donne trois niveaux — plein, contour,
        discret — ce qui dit l'ordre sans l'ecrire.

        Ils restent des BASCULES et non des navigations : le formulaire correspondant s'ouvre
        dessous, dans la meme colonne. `aria-expanded` porte l'etat plutot qu'`aria-current`,
        qui designe une page courante — ce qu'aucun des trois n'est.

        Un second clic referme. Ouvrir un formulaire sans pouvoir le refermer oblige a recharger
        la page pour revenir a la fiche.
      */}
      <div className="fiche__actions-relation">
        <Bouton
          taille="lg"
          bloc
          onClick={() => setVue(vue === 'candidature' ? null : 'candidature')}
        >
          {t.candidater}
        </Bouton>

        <Bouton
          apparence="secondaire"
          bloc
          onClick={() => setVue(vue === 'message' ? null : 'message')}
        >
          <Icone nom="message" taille="sm" />
          {t.contacter}
        </Bouton>

        {/*
          **Proposer une visite depuis la fiche** — ticket `fo-annonce-01`. Le geste existait
          depuis L4, mais seulement depuis l'ecran des candidatures : il fallait donc candidater
          pour pouvoir demander a visiter, c'est-a-dire s'engager avant d'avoir vu. C'est
          l'inverse de l'ordre naturel, et le bandeau anti-arnaque juste en dessous dit
          precisement de visiter avant de s'engager.

          `candidatureId` est nul ici, et le domaine l'accepte : une visite peut preceder toute
          candidature.
        */}
        <Bouton
          apparence="discret"
          bloc
          onClick={() => setVue(vue === 'visite' ? null : 'visite')}
        >
          <Icone nom="calendrier" taille="sm" />
          {t.proposerVisite}
        </Bouton>
      </div>

      {vue === 'message' && (
        <form
          onSubmit={(evenement) => {
            evenement.preventDefault()
            void contacter()
          }}
        >
          {erreur !== null && <Bandeau ton="erreur">{erreur}</Bandeau>}

          <Zone id="premier-message" libelle={t.premierMessage} valeur={corps} onChange={setCorps} />

          <button type="submit" className="bouton-principal" disabled={envoi || corps.trim().length === 0}>
            {envoi ? t.chargement : t.envoyerMessage}
          </button>
        </form>
      )}

      {vue === 'candidature' && <FormulaireCandidature langue={langue} annonceId={id} />}

      {vue === 'visite' && (
        <ProposerVisite langue={langue} annonceId={id} candidatureId={null} />
      )}
    </section>
  )
}

/**
 * Partager la fiche — ticket `fo-annonce-01`.
 *
 * **Le partage natif quand il existe, le presse-papier sinon.** `navigator.share` ouvre la feuille
 * du système — WhatsApp en tête, qui est le canal réel de ce marché. Là où il n'existe pas (tous
 * les navigateurs de bureau, sauf Safari), copier le lien est le geste équivalent, et le dire est
 * indispensable : une copie silencieuse se lit comme un clic sans effet.
 *
 * L'URL partagée est celle de la barre d'adresse, préfixe de langue compris : un lien envoyé par
 * un arabophone s'ouvre en arabe.
 */
function Partager({ titre }: { titre: string }) {
  const t = useTextes()
  const [copie, setCopie] = useState(false)

  const partager = async () => {
    const url = globalThis.location.href

    if (typeof navigator.share === 'function') {
      try {
        await navigator.share({ title: titre, url })
        return
      } catch {
        // Partage refusé ou annulé — on retombe sur la copie, qui ne peut pas être annulée.
      }
    }

    try {
      await navigator.clipboard.writeText(url)
      setCopie(true)
      globalThis.setTimeout(() => setCopie(false), 2500)
    } catch {
      // Presse-papier refusé (contexte non sécurisé) : rien à faire, et rien à dire de faux.
    }
  }

  return (
    /* `bouton bouton--discret bouton--sm`, la forme de la maquette — ticket `fo-annonce-12`. La
       puce était une étiquette d'information détournée en commande : même hauteur qu'un libellé,
       aucune surface au survol, et rien qui la distingue des pastilles « Photos originales »
       posées juste en dessous. */
    <button type="button" className="bouton bouton--discret bouton--sm" onClick={() => void partager()}>
      <Icone nom="export" taille="sm" />
      {copie ? t.annonceLienCopie : t.annoncePartager}
    </button>
  )
}

/**
 * Garder l'annonce — ticket `fo-annonce-01`.
 *
 * **Le même geste que le cœur d'une vignette, avec son libellé.** Sur une carte de résultat, un
 * cœur suffit : il y en a douze, et la place manque. Sur une fiche, il n'y a qu'un objet et de la
 * place — le mot dit ce que le pictogramme laisse deviner.
 *
 * L'état initial vient du serveur (`isFavorite`), sans quoi le bouton proposerait d'enregistrer ce
 * qui l'est déjà, et le clic suivant le retirerait. La mise à jour est optimiste : sur 3G,
 * attendre l'aller-retour donne un bouton qui met une seconde à réagir, et on appuie deux fois.
 */
function Enregistrer({
  id,
  initial,
  connecte,
}: {
  id: string
  initial: boolean
  connecte: boolean
}) {
  const t = useTextes()
  const navigate = useNavigate()
  const [garde, setGarde] = useState(initial)

  const basculer = async () => {
    if (!connecte) {
      // On garde l'intention : perdre le geste, c'est perdre le compte. Même chemin que le cœur
      // d'une vignette.
      globalThis.sessionStorage.setItem('coloc:favori-en-attente', id)
      navigate(`/connexion?retour=${encodeURIComponent(`/annonces/${id}`)}`)
      return
    }

    const vise = !garde
    setGarde(vise)

    try {
      await (vise ? ajouterFavori(id) : retirerFavori(id))
    } catch {
      setGarde(!vise)
    }
  }

  return (
    <button
      type="button"
      className="bouton bouton--discret bouton--sm"
      aria-pressed={garde}
      onClick={() => void basculer()}
    >
      <Icone nom="coeur" taille="sm" />
      {garde ? t.favoriRetirer : t.annonceEnregistrer}
    </button>
  )
}

/**
 * La galerie de la fiche — mosaïque au large, défilement par à-coups en compact.
 *
 * **Un seul DOM, deux mises en page.** Au-delà de 60 rem c'est une mosaïque : une grande photo et
 * quatre petites, ce qui donne à la fiche son premier coup d'œil. En dessous, la même grille
 * devient une piste `scroll-snap` — le geste tactile attendu, sans une ligne de JavaScript.
 * Écrire deux composants aurait produit deux comportements qui divergent.
 *
 * **RTL par construction** (invariant 11) : le défilement natif inverse son sens tout seul en
 * arabe. Un carrousel animé à la main aurait rapporté `left` et `right` par la porte de service —
 * c'est l'endroit classique où le RTL se rétrofite.
 *
 * Au-delà de cinq photos, les suivantes vivent dans une feuille. Personne ne fait défiler
 * quatorze vignettes dans un en-tête ; on les ouvre quand on a décidé que l'annonce méritait
 * qu'on s'y attarde.
 */
function Galerie({ photos }: { photos: { mediaId: string; caption: string | null }[] }) {
  const t = useTextes()
  const langue = useLangue()

  /*
    **Le rang ouvert, et non un booléen** — tickets `fo-annonce-04` et `fo-annonce-07`.

    La visionneuse s'ouvre SUR une photo, pas sur la galerie : cliquer la troisième vignette et
    tomber sur la première est le défaut que le second ticket décrit. Un rang porte les deux
    informations — ouverte, et où — là où un booléen plus un index se seraient désynchronisés.

    `null` = fermée. C'est ce qui permet au rang 0 d'être une valeur ouverte comme une autre ;
    avec un `-1` sentinelle, la première photo aurait fini par ne pas s'ouvrir.
  */
  const [ouverte, setOuverte] = useState<number | null>(null)

  if (photos.length === 0) {
    return <p className="fiche__sans-photo">{t.annonceSansPhoto}</p>
  }

  const visibles = photos.slice(0, 5)

  return (
    <>
      <div className={`mosaique mosaique--${visibles.length}`}>
        {/* Chaque case est un BOUTON — ticket `fo-annonce-07`. Une photo qu'on ne peut pas
            agrandir est le geste que tout le monde tente en premier, et il ne se devine pas
            depuis un `<div>`. Le curseur et le focus viennent avec, gratuitement. */}
        {visibles.map((photo, rang) => (
          <button
            type="button"
            className="mosaique__case"
            key={photo.mediaId}
            onClick={() => setOuverte(rang)}
            aria-label={`${t.annonceAgrandir} ${nombre(rang + 1, langue)}`}
          >
            <Image
              mediaId={photo.mediaId}
              /* La première photo porte le LCP : jamais différée, et demandée en priorité. */
              prioritaire={rang === 0}
              ratio="remplir"
              sizes={rang === 0 ? '(min-width: 60rem) 46rem, 96vw' : '(min-width: 60rem) 22rem, 96vw'}
              alt={photo.caption ?? ''}
            />
          </button>
        ))}

        {photos.length > 1 && (
          <button
            type="button"
            className="puce puce--action mosaique__tout"
            onClick={() => setOuverte(0)}
          >
            <Icone nom="image" taille="sm" />
            {`${t.annonceVoirPhotos} (${nombre(photos.length, langue)})`}
          </button>
        )}
      </div>

      {ouverte !== null && (
        <Visionneuse photos={photos} rang={ouverte} onRang={setOuverte} onFermer={() => setOuverte(null)} />
      )}
    </>
  )
}

/**
 * La visionneuse — une photo en grand, et de quoi passer à la suivante.
 *
 * Ticket `fo-annonce-04`. Ce qu'elle remplace était une grille de vignettes dans une modale :
 * on y voyait toutes les photos, et aucune. Regarder un logement, c'est regarder UNE pièce, puis
 * la suivante — la grille servait l'inventaire, pas la visite.
 *
 * **Trois façons de naviguer, parce qu'il y a trois façons d'être là** : les flèches du clavier,
 * les boutons à l'écran, et — pour le tactile — le fait que l'image entière avance d'un cran au
 * clic. La touche Échap ferme, comme partout ailleurs.
 *
 * **Le compteur n'est pas décoratif.** « 3 / 14 » est ce qui dit qu'il reste onze photos ; sans
 * lui, on ne sait pas si l'on a fait le tour, et on referme trop tôt.
 *
 * Le défilement en boucle est volontaire : arriver au bout d'une galerie de photos et se heurter
 * à un bouton mort n'apprend rien, alors que revenir au début dit « vous avez tout vu ».
 */
function Visionneuse({
  photos,
  rang,
  onRang,
  onFermer,
}: {
  photos: { mediaId: string; caption: string | null }[]
  rang: number
  onRang: (rang: number) => void
  onFermer: () => void
}) {
  const t = useTextes()
  const langue = useLangue()

  const deplacer = useCallback(
    (pas: number) => onRang((rang + pas + photos.length) % photos.length),
    [onRang, photos.length, rang],
  )

  /*
    Les flèches du clavier. `Modale` gère déjà Échap et le piège à focus ; ce qu'elle ne peut pas
    savoir, c'est qu'il y a ici une notion de précédent et de suivant.

    En arabe, la flèche « gauche » doit avancer : la direction de lecture est inversée, et une
    flèche qui recule quand on la voit avancer est le genre de détail qui rend une interface
    étrangère à elle-même (invariant 11 — le RTL ne se rétrofite pas, y compris au clavier).
  */
  useEffect(() => {
    const rtl = document.documentElement.dir === 'rtl'

    const auClavier = (evenement: KeyboardEvent) => {
      if (evenement.key === 'ArrowRight') {
        deplacer(rtl ? -1 : 1)
      } else if (evenement.key === 'ArrowLeft') {
        deplacer(rtl ? 1 : -1)
      }
    }

    document.addEventListener('keydown', auClavier)

    return () => document.removeEventListener('keydown', auClavier)
  }, [deplacer])

  const photo = photos[rang]
  const legende = photo.caption ?? `${t.annoncePhotos} ${nombre(rang + 1, langue)}`

  return (
    <Modale
      titre={`${nombre(rang + 1, langue)} / ${nombre(photos.length, langue)}`}
      large
      onFermer={onFermer}
    >
      <div className="visionneuse">
        <button
          type="button"
          className="visionneuse__fleche"
          onClick={() => deplacer(-1)}
          aria-label={t.annoncePhotoPrecedente}
          disabled={photos.length < 2}
        >
          <Icone nom="chevron" arriere />
        </button>

        {/* L'image avance au clic : sur un écran tactile, les flèches sont petites et le pouce
            est gros. Ce n'est pas un bouton — la légende resterait sélectionnable — mais une
            zone qui réagit, et les flèches restent là pour qui navigue autrement. */}
        <div
          className="visionneuse__scene"
          onClick={() => deplacer(1)}
          role="presentation"
        >
          <Image mediaId={photo.mediaId} ratio="remplir" sizes="90vw" alt={legende} />
        </div>

        <button
          type="button"
          className="visionneuse__fleche"
          onClick={() => deplacer(1)}
          aria-label={t.annoncePhotoSuivante}
          disabled={photos.length < 2}
        >
          <Icone nom="chevron" />
        </button>
      </div>

      {photo.caption !== null && <p className="visionneuse__legende">{photo.caption}</p>}

      {/* Les vignettes : elles disent combien il en reste et permettent d'aller droit à celle
          qu'on veut revoir. La courante est marquée par `aria-current`, dont le CSS se sert —
          une seule source pour l'état visuel et pour le lecteur d'écran. */}
      {photos.length > 1 && (
        <div className="visionneuse__pellicule">
          {photos.map((autre, index) => (
            <button
              type="button"
              key={autre.mediaId}
              className="visionneuse__vignette"
              aria-current={index === rang ? 'true' : undefined}
              aria-label={`${t.annonceAgrandir} ${nombre(index + 1, langue)}`}
              onClick={() => onRang(index)}
            >
              <Image mediaId={autre.mediaId} ratio="remplir" sizes="6rem" alt="" />
            </button>
          ))}
        </div>
      )}
    </Modale>
  )
}
